import React, { useEffect, forwardRef, useImperativeHandle } from 'react';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Node } from '@tiptap/core';
import { Underline } from '@tiptap/extension-underline';
import { TextAlign } from '@tiptap/extension-text-align';
import { Highlight } from '@tiptap/extension-highlight';
import { Link } from '@tiptap/extension-link';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { OrderedList } from '@tiptap/extension-ordered-list';
import { Mention } from '@tiptap/extension-mention';
import { createMentionSuggestion } from './mentionSuggestion';
import { type VariableOption } from './mentionSuggestion';
import {
    Bold,
    Columns3,
    Italic,
    Strikethrough,
    Underline as UnderlineIcon,
    List,
    ListOrdered,
    Highlighter,
    AlignLeft,
    AlignCenter,
    AlignRight,
    Rows3,
    Table as TableIcon,
    Trash2,
    ChevronDown,
    Link2,
    Unlink,
    Check,
    Minus,
    Plus,
} from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../ui/tooltip';
import type { DocumentRichTextRef } from './DocumentRichText';

const VALID_OL_TYPES = ['1', 'a', 'A', 'i', 'I'];

// Extends OrderedList to support the type attribute (e.g. type="a" for lower-alpha)
const TypedOrderedList = OrderedList.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            type: {
                default: null,
                parseHTML: (element) => {
                    const type = element.getAttribute('type');
                    if (type && VALID_OL_TYPES.includes(type)) return type;
                    return null;
                },
                renderHTML: (attributes) => {
                    if (!attributes.type) return {};
                    return { type: attributes.type };
                },
            },
        };
    },
});

interface DocumentRichTextEnhancedProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    extensions?: Node[];
    variableOptions?: VariableOption[];
}

interface EnhancedMenuBarProps {
    editor: Editor | null;
    disabled?: boolean;
}

const ToolbarButton: React.FC<{
    onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
    isActive?: boolean;
    disabled?: boolean;
    tooltip: string;
    children: React.ReactNode;
}> = ({ onClick, isActive, disabled, children, tooltip }) => (
    <Tooltip>
        <TooltipTrigger asChild>
            <button
                type="button"
                onClick={onClick}
                disabled={disabled}
                className={cn(
                    'p-1.5 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
                    'hover:bg-accent hover:text-accent-foreground',
                    isActive && 'bg-muted text-foreground'
                )}
                aria-label={tooltip}
            >
                {children}
            </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" sideOffset={4}>
            {tooltip}
        </TooltipContent>
    </Tooltip>
);

const ToolbarDivider = () => (
    <div className="w-px h-5 bg-border/50 mx-1" />
);

const TableActionIcon: React.FC<{
    type: 'row' | 'column';
    action: 'add' | 'remove';
}> = ({ type, action }) => {
    const BaseIcon = type === 'column' ? Columns3 : Rows3;
    const BadgeIcon = action === 'add' ? Plus : Minus;

    return (
        <span className="relative flex h-4 w-4 items-center justify-center">
            <BaseIcon className="h-4 w-4" />
            <span className="absolute -right-1 -top-1 rounded-full bg-background">
                <BadgeIcon className="h-2.5 w-2.5" />
            </span>
        </span>
    );
};

const normalizeLinkUrl = (rawUrl: string) => {
    const trimmedUrl = rawUrl.trim();

    if (!trimmedUrl) {
        return '';
    }

    return /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmedUrl)
        ? trimmedUrl
        : `https://${trimmedUrl}`;
};

const EnhancedMenuBar: React.FC<EnhancedMenuBarProps> = ({ editor, disabled }) => {
    const [headingOpen, setHeadingOpen] = React.useState(false);
    const [linkOpen, setLinkOpen] = React.useState(false);
    const [linkUrl, setLinkUrl] = React.useState('');
    const headingRef = React.useRef<HTMLDivElement>(null);
    const linkRef = React.useRef<HTMLDivElement>(null);
    const linkInputRef = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (headingRef.current && !headingRef.current.contains(e.target as globalThis.Node)) {
                setHeadingOpen(false);
            }
            if (linkRef.current && !linkRef.current.contains(e.target as globalThis.Node)) {
                setLinkOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const applyLink = () => {
        if (!editor || !linkUrl) { setLinkOpen(false); setLinkUrl(''); return; }
        const normalizedUrl = normalizeLinkUrl(linkUrl);

        if (!normalizedUrl) {
            setLinkOpen(false);
            setLinkUrl('');
            return;
        }

        const { from, to } = editor.state.selection;
        if (from === to) {
            editor.chain().focus()
            .insertContent({ type: 'text', text: linkUrl.trim(), marks: [{ type: 'link', attrs: { href: normalizedUrl } }] })
            .run();
        } else {
            editor.chain().focus().extendMarkRange('link').setLink({ href: normalizedUrl }).run();
        }
        setLinkOpen(false);
        setLinkUrl('');
    };

    if (!editor) return null;

    const activeHeading = ([1, 2, 3, 4, 5, 6] as const).find((level) =>
        editor.isActive('heading', { level })
    );

    const headingLabel = activeHeading ? `H${activeHeading}` : 'Normal';

    return (
        <TooltipProvider delayDuration={300}>
            <div className="z-20 shrink-0 border-b border-input/50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
                <div className="flex flex-wrap items-center gap-0.5 p-2">
                    {/* Heading dropdown */}
                    <div className="relative" ref={headingRef}>
                        <button
                            type="button"
                            disabled={disabled}
                            onClick={(e) => {
                                e.preventDefault();
                                setHeadingOpen(!headingOpen);
                            }}
                            className={cn(
                                'flex items-center gap-1 px-2 py-1.5 rounded text-sm hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
                                activeHeading && 'bg-muted text-foreground'
                            )}
                            aria-label="Heading level"
                        >
                            <span className="min-w-[3.5rem] text-left">{headingLabel}</span>
                            <ChevronDown className="h-3 w-3" />
                        </button>
                        {headingOpen && (
                            <div className="absolute top-full left-0 mt-1 z-50 min-w-[8rem] rounded-md border border-border bg-popover py-1 shadow-md">
                                <button
                                    type="button"
                                    className={cn(
                                        'w-full text-left px-3 py-1.5 text-sm hover:bg-muted transition-colors',
                                        !activeHeading && 'bg-muted'
                                    )}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        editor.chain().focus().setParagraph().run();
                                        setHeadingOpen(false);
                                    }}
                                >
                                    Normal
                                </button>
                                {([1, 2, 3, 4, 5, 6] as const).map((level) => (
                                    <button
                                        key={level}
                                        type="button"
                                        className={cn(
                                            'w-full text-left px-3 py-1.5 hover:bg-muted transition-colors',
                                            activeHeading === level && 'bg-muted'
                                        )}
                                        style={{ fontSize: `${1.25 - level * 0.1}rem`, fontWeight: 600 }}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            editor.chain().focus().toggleHeading({ level }).run();
                                            setHeadingOpen(false);
                                        }}
                                    >
                                        Heading {level}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <ToolbarDivider />

                    <ToolbarButton
                        onClick={(e) => { e.preventDefault(); editor.chain().focus().toggleBold().run(); }}
                        isActive={editor.isActive('bold')}
                        disabled={disabled}
                        tooltip="Bold (Ctrl+B)"
                    >
                        <Bold className="h-4 w-4" />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={(e) => { e.preventDefault(); editor.chain().focus().toggleItalic().run(); }}
                        isActive={editor.isActive('italic')}
                        disabled={disabled}
                        tooltip="Italic (Ctrl+I)"
                    >
                        <Italic className="h-4 w-4" />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={(e) => { e.preventDefault(); editor.chain().focus().toggleStrike().run(); }}
                        isActive={editor.isActive('strike')}
                        disabled={disabled}
                        tooltip="Strikethrough (Ctrl+Shift+S)"
                    >
                        <Strikethrough className="h-4 w-4" />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={(e) => { e.preventDefault(); editor.chain().focus().toggleUnderline().run(); }}
                        isActive={editor.isActive('underline')}
                        disabled={disabled}
                        tooltip="Underline (Ctrl+U)"
                    >
                        <UnderlineIcon className="h-4 w-4" />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={(e) => { e.preventDefault(); editor.chain().focus().toggleHighlight().run(); }}
                        isActive={editor.isActive('highlight')}
                        disabled={disabled}
                        tooltip="Highlight"
                    >
                        <Highlighter className="h-4 w-4" />
                    </ToolbarButton>
                    <div className="relative" ref={linkRef}>
                        <ToolbarButton
                            onClick={(e) => {
                                e.preventDefault();
                                if (editor.isActive('link')) {
                                    editor.chain().focus().unsetLink().run();
                                    return;
                                }
                                const existingHref = editor.getAttributes('link').href ?? '';
                                setLinkUrl(existingHref);
                                setLinkOpen(true);
                                setTimeout(() => linkInputRef.current?.focus(), 0);
                            }}
                            isActive={editor.isActive('link')}
                            disabled={disabled}
                            tooltip={editor.isActive('link') ? 'Remove Link' : 'Add Link'}
                        >
                            {editor.isActive('link') ? <Unlink className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}
                        </ToolbarButton>
                        {linkOpen && (
                            <div className="absolute top-full left-0 mt-1 z-50 flex items-center gap-1.5 rounded-md border border-border bg-popover p-2 shadow-md">
                                <input
                                    ref={linkInputRef}
                                    type="url"
                                    value={linkUrl}
                                    onChange={(e) => setLinkUrl(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') { e.preventDefault(); applyLink(); }
                                        if (e.key === 'Escape') { setLinkOpen(false); setLinkUrl(''); }
                                    }}
                                    placeholder="https://..."
                                    className="h-7 w-52 rounded border border-input bg-background px-2 text-sm outline-none focus:ring-1 focus:ring-ring"
                                />
                                <button
                                    type="button"
                                    onClick={applyLink}
                                    className="flex h-7 w-7 items-center justify-center rounded bg-primary text-primary-foreground transition-colors hover:bg-primary/90"
                                >
                                    <Check className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        )}
                    </div>

                    <ToolbarDivider />

                    <ToolbarButton
                        onClick={(e) => { e.preventDefault(); editor.chain().focus().toggleBulletList().run(); }}
                        isActive={editor.isActive('bulletList')}
                        disabled={disabled}
                        tooltip="Bullet List (Ctrl+Shift+8)"
                    >
                        <List className="h-4 w-4" />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={(e) => { e.preventDefault(); editor.chain().focus().toggleOrderedList().run(); }}
                        isActive={editor.isActive('orderedList')}
                        disabled={disabled}
                        tooltip="Ordered List (Ctrl+Shift+7)"
                    >
                        <ListOrdered className="h-4 w-4" />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={(e) => { e.preventDefault(); editor.chain().focus().setHorizontalRule().run(); }}
                        disabled={disabled}
                        tooltip="Horizontal Rule"
                    >
                        <Minus className="h-4 w-4" />
                    </ToolbarButton>

                    <ToolbarDivider />

                    <ToolbarButton
                        onClick={(e) => { e.preventDefault(); editor.chain().focus().setTextAlign('left').run(); }}
                        isActive={editor.isActive({ textAlign: 'left' })}
                        disabled={disabled}
                        tooltip="Align Left"
                    >
                        <AlignLeft className="h-4 w-4" />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={(e) => { e.preventDefault(); editor.chain().focus().setTextAlign('center').run(); }}
                        isActive={editor.isActive({ textAlign: 'center' })}
                        disabled={disabled}
                        tooltip="Align Center"
                    >
                        <AlignCenter className="h-4 w-4" />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={(e) => { e.preventDefault(); editor.chain().focus().setTextAlign('right').run(); }}
                        isActive={editor.isActive({ textAlign: 'right' })}
                        disabled={disabled}
                        tooltip="Align Right"
                    >
                        <AlignRight className="h-4 w-4" />
                    </ToolbarButton>

                    <ToolbarDivider />

                    <ToolbarButton
                        onClick={(e) => { e.preventDefault(); editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(); }}
                        disabled={disabled}
                        tooltip="Insert Table"
                    >
                        <TableIcon className="h-4 w-4" />
                    </ToolbarButton>
                    {editor.isActive('table') && (
                        <>
                            <ToolbarButton
                                onClick={(e) => { e.preventDefault(); editor.chain().focus().addColumnAfter().run(); }}
                                disabled={disabled}
                                tooltip="Add Column"
                            >
                                <TableActionIcon type="column" action="add" />
                            </ToolbarButton>
                            <ToolbarButton
                                onClick={(e) => { e.preventDefault(); editor.chain().focus().deleteColumn().run(); }}
                                disabled={disabled}
                                tooltip="Remove Column"
                            >
                                <TableActionIcon type="column" action="remove" />
                            </ToolbarButton>
                            <ToolbarButton
                                onClick={(e) => { e.preventDefault(); editor.chain().focus().addRowAfter().run(); }}
                                disabled={disabled}
                                tooltip="Add Row"
                            >
                                <TableActionIcon type="row" action="add" />
                            </ToolbarButton>
                            <ToolbarButton
                                onClick={(e) => { e.preventDefault(); editor.chain().focus().deleteRow().run(); }}
                                disabled={disabled}
                                tooltip="Remove Row"
                            >
                                <TableActionIcon type="row" action="remove" />
                            </ToolbarButton>
                            <ToolbarButton
                                onClick={(e) => { e.preventDefault(); editor.chain().focus().deleteTable().run(); }}
                                disabled={disabled}
                                tooltip="Delete Table"
                            >
                                <Trash2 className="h-3 w-3" />
                            </ToolbarButton>
                        </>
                    )}
                </div>
            </div>
        </TooltipProvider>
    );
};

export const DocumentRichTextEnhanced = forwardRef<
    DocumentRichTextRef,
    DocumentRichTextEnhancedProps
>(({ value, onChange, placeholder, disabled, className, extensions = [], variableOptions = [] }, ref) => {
    const mentionSuggestion = React.useMemo(
        () => createMentionSuggestion(variableOptions),
        [variableOptions]
    );

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: {
                    levels: [1, 2, 3, 4, 5, 6],
                },
                orderedList: false,
            }),
            TypedOrderedList,
            Underline,
            TextAlign.configure({
                types: ['heading', 'paragraph'],
                alignments: ['left', 'center', 'right'],
            }),
            Highlight.configure({
                HTMLAttributes: {
                    style: 'background-color: rgba(245, 158, 11, 0.3)',
                },
            }),
            Link.configure({
                openOnClick: false,
                autolink: true,
                HTMLAttributes: {
                    class: 'editor-link',
                    target: '_blank',
                    rel: 'noopener noreferrer',
                },
            }),
            Table.configure({
                resizable: true,
            }),
            TableRow,
            TableCell,
            TableHeader,
            ...(variableOptions.length > 0
                ? [Mention.configure({
                    HTMLAttributes: {
                        class: 'mention',
                    },
                    renderHTML({ node }) {
                        return [
                            'span',
                            { 'data-type': 'mention', 'data-id': node.attrs.id, class: 'mention' },
                            node.attrs.label ?? node.attrs.id,
                        ];
                    },
                    suggestion: mentionSuggestion,
                })]
                : []),
            ...extensions,
        ],
        content: value || '',
        editable: !disabled,
        editorProps: {
            attributes: {
                class: 'outline-none min-h-[400px] px-3 py-2 text-sm',
            },
        },
        onUpdate({ editor }) {
            onChange(editor.getHTML());
        },
    });

    useImperativeHandle(ref, () => ({
        editor,
    }));

    useEffect(() => {
        if (editor && value !== editor.getHTML()) {
            editor.commands.setContent(value || '');
        }
    }, [value, editor]);

    useEffect(() => {
        if (editor) {
            editor.setEditable(!disabled);
        }
    }, [disabled, editor]);

    return (
        <div
            className={cn(
                'flex max-h-[70vh] w-full flex-col overflow-hidden rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
                disabled && 'opacity-50 cursor-not-allowed',
                className
            )}
        >
            <EnhancedMenuBar editor={editor} disabled={disabled} />
            <EditorContent
                editor={editor}
                className={cn(
                    'min-h-0 flex-1 overflow-y-auto',
                    '[&::-webkit-scrollbar]:w-2',
                    '[&::-webkit-scrollbar-track]:bg-transparent',
                    '[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-border/50 [&:hover::-webkit-scrollbar-thumb]:bg-border',
                    '[&_.ProseMirror]:outline-none',
                    '[&_.ProseMirror_p]:text-[15px] [&_.ProseMirror_p]:leading-7 [&_.ProseMirror_p]:font-normal [&_.ProseMirror_p]:text-foreground/90',
                    '[&_.ProseMirror_p:empty]:min-h-[1.75rem]',
                    '[&_.ProseMirror_ul]:text-[15px] [&_.ProseMirror_ul]:font-normal [&_.ProseMirror_ul]:text-foreground/90',
                    '[&_.ProseMirror_ol]:text-[15px] [&_.ProseMirror_ol]:font-normal [&_.ProseMirror_ol]:text-foreground/90',
                    '[&_.ProseMirror_li]:leading-7 [&_.ProseMirror_li]:font-normal [&_.ProseMirror_li]:text-foreground/90',
                    '[&_.ProseMirror_em]:italic',
                    '[&_.ProseMirror_s]:line-through',
                    '[&_.ProseMirror_strong]:font-bold',
                    '[&_.ProseMirror_u]:underline',
                    '[&_.ProseMirror_mark]:bg-yellow-200/40 [&_.ProseMirror_mark]:rounded-sm [&_.ProseMirror_mark]:px-0.5',
                    '[&_.ProseMirror_table]:border-collapse [&_.ProseMirror_table]:w-full [&_.ProseMirror_table]:my-2',
                    '[&_.ProseMirror_td]:border [&_.ProseMirror_td]:border-border/50 [&_.ProseMirror_td]:p-2 [&_.ProseMirror_td]:min-w-[80px]',
                    '[&_.ProseMirror_th]:border [&_.ProseMirror_th]:border-border/50 [&_.ProseMirror_th]:p-2 [&_.ProseMirror_th]:font-semibold [&_.ProseMirror_th]:bg-muted/30',
                    '[&_.ProseMirror_.mention]:bg-primary/15 [&_.ProseMirror_.mention]:text-primary [&_.ProseMirror_.mention]:rounded [&_.ProseMirror_.mention]:px-1 [&_.ProseMirror_.mention]:py-0.5 [&_.ProseMirror_.mention]:text-sm [&_.ProseMirror_.mention]:font-medium',
                    '[&_.ProseMirror_.editor-link]:text-blue-400 [&_.ProseMirror_.editor-link]:underline [&_.ProseMirror_.editor-link]:cursor-pointer',
                    '[&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:ml-4',
                    '[&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:ml-4',
                    '[&_.ProseMirror_ol[type=a]]:list-[lower-alpha]',
                    '[&_.ProseMirror_ol[type=A]]:list-[upper-alpha]',
                    '[&_.ProseMirror_ol[type=i]]:list-[lower-roman]',
                    '[&_.ProseMirror_ol[type=I]]:list-[upper-roman]',
                    '[&_.ProseMirror_h1]:text-2xl [&_.ProseMirror_h1]:font-bold [&_.ProseMirror_h1]:mb-2 [&_.ProseMirror_h1]:mt-4',
                    '[&_.ProseMirror_h2]:text-xl [&_.ProseMirror_h2]:font-bold [&_.ProseMirror_h2]:mb-2 [&_.ProseMirror_h2]:mt-3',
                    '[&_.ProseMirror_h3]:text-lg [&_.ProseMirror_h3]:font-semibold [&_.ProseMirror_h3]:mb-1.5 [&_.ProseMirror_h3]:mt-3',
                    '[&_.ProseMirror_h4]:text-base [&_.ProseMirror_h4]:font-semibold [&_.ProseMirror_h4]:mb-1 [&_.ProseMirror_h4]:mt-2',
                    '[&_.ProseMirror_h5]:text-sm [&_.ProseMirror_h5]:font-semibold [&_.ProseMirror_h5]:mb-1 [&_.ProseMirror_h5]:mt-2',
                    '[&_.ProseMirror_h6]:text-sm [&_.ProseMirror_h6]:font-medium [&_.ProseMirror_h6]:mb-1 [&_.ProseMirror_h6]:mt-2',
                    '[&_.ProseMirror_hr]:my-4 [&_.ProseMirror_hr]:border-0 [&_.ProseMirror_hr]:border-t [&_.ProseMirror_hr]:border-border/60',
                    '[&_.ProseMirror_p.is-editor-empty:first-child]:before:content-[attr(data-placeholder)]',
                    '[&_.ProseMirror_p.is-editor-empty:first-child]:before:text-muted-foreground',
                    '[&_.ProseMirror_p.is-editor-empty:first-child]:before:float-left',
                    '[&_.ProseMirror_p.is-editor-empty:first-child]:before:h-0',
                    '[&_.ProseMirror_p.is-editor-empty:first-child]:before:pointer-events-none'
                )}
            />
        </div>
    );
});

DocumentRichTextEnhanced.displayName = 'DocumentRichTextEnhanced';
